




















































































































































import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import CropsTableController from "../crops-table/CropsTableController.vue";
import InteractiveMapController from "../interactive-map/InteractiveMapController.vue";
import * as resultsService from "@/services/results/index";
import * as statusService from "@/services/analyses_status/index";
import { Analysis, FarmAnalysis } from "@/api/analyses/types";
import { FarmAnalysisResult } from "@/api/results/types";
import { AnalysisStatus } from "@/api/analyses_status/types";
import ResultSummary from "../result-summary/ResultSummary.vue";
import GeneralSummary from "../general-summary/GeneralSummary.vue";
import NdviChartHc from "@/components/ndvi-chart/NdviChartHc.vue";
import { CropsDTO } from "../../../../api/crops/types";
import { wrapGeometryObject } from "@/helpers/geojson";
import * as weatherService from "@/services/weather/index";
import { WeatherData } from "@/api/weather/types";
import TemperatureChart from "@/components/temperature-chart/TemperatureChartHCharts.vue";
import SPI4Chart from "@/components/spi4-chart/SPI4ChartHCharts.vue";
import "vue-nav-tabs/themes/vue-tabs.css";
import CarInfractions from "@/views/environmental/components/car-infractions/CarInfractions.vue";
import AdminOnly from '@/components/adminOnly/AdminOnly.vue';
import * as paceService from '@/services/pace';
import PaceChartHc from '@/components/pace-chart/PaceChartHc.vue';
import {AnalysisPaceDTO} from "@/api/pace/types";
import Loading from '@/components/loading/Loading.vue';


const COLORS = ["#eecb11", "#fa14ff", "#00fca1", "#0008fc", "#42f5ff"];
const LEN_COLORS = COLORS.length;

@Component({
  name: "AnalysesViewer",
  components: {
    CropsTableController,
    InteractiveMap: InteractiveMapController,
    ResultSummary,
    NdviChartHc,
    TemperatureChart,
    SPI4Chart,
    CarInfractions,
    GeneralSummary,
    AdminOnly,
    PaceChartHc,
    Loading,
  },
})
export default class AnalysesViewer extends Vue {
  @Prop({ type: Object, required: true })
  private farmAnalysis!: FarmAnalysis;

  @Prop({ type: Number, default: () => Math.floor(Math.random() * LEN_COLORS) })
  private colorIndex!: number;

  @Prop({ type: Boolean, required: true })
  private activeTab!: boolean;

  @Prop({ type: String, default: "" }) 
  private source?: string;

  private infractionsGeojson: Object[] = [];

  private selectedCrops: string[] = [];
  private result: FarmAnalysisResult | null = null;
  private results: FarmAnalysisResult[] = [];
  private status: AnalysisStatus | null = null;
  private url: string = "";

  private hovered: string | null = null;
  private polling: number | null = null;

  private fromDate: Date | null = null;
  private untilDate: Date | null = null;
  private hasSummer: boolean = false;
  private loading: boolean = false;
  private annualPace : AnalysisPaceDTO | null = null;
  private cropTypePace : AnalysisPaceDTO | null = null;


  private get analysis(): Analysis {
    return this.$store.state.analyses.analysesContext;
  }

  get harvestSeason(): string {
    const value = this.$route.query.harvestSeason;
    if (value === 'SUMMER' || value === 'WINTER') return value;
    return this.analysis.harvestSeason;
  }

  get currentCropType(): string {
    if (this.analysis.unified) {
      return this.harvestSeason === 'WINTER'
        ? (this.analysis.winterCropType || 'CORN')
        : (this.analysis.summerCropType || this.analysis.cropType);
    }
    return this.harvestSeason === 'WINTER' && this.analysis.cropType === 'SOY'
      ? 'COTTON'
      : this.analysis.cropType;
  }

  @Watch('harvestSeason')
  private onHarvestSeasonChange() {
    if (this.activeTab) {
      this.loadResult(true);
    }
  }

  @Watch('activeTab')
  private onActiveTabChange(active: boolean) {
    if (active) {
      this.loadResult(true);
    }
  }

  @Watch('hasSummer', { immediate: true })
  private emitHasSummer(value: boolean) {
    this.$emit('update:hasSummer', {
      year: this.farmAnalysis.analysisYear,
      hasSummer: value,
    });
  }

  private async mounted() {
    try {
      this.hasSummer = (await resultsService.getFarmAnalysisResultList(this.farmAnalysis.uuid, 'SUMMER')).length > 0;
    } catch(err) {
    }
    
    await this.loadResult();
    await this.loadStatus();
    this.loadWeatherData();
    if (this.needsTracking) {
      this.trackResult();
    }
    await this.loadPace()
  }
  
  private async loadPace() {
    if (this.$store.getters["user/isAdminUser"]) {
      try {
        this.annualPace = await paceService.getByUuid(this.farmAnalysis.uuid, 'ANNUAL');
        this.cropTypePace = await paceService.getByUuid(this.farmAnalysis.uuid, this.farmAnalysis.cropType);
      } catch (err) {
      }
    }
  }
  
  private showPlots(event: object) {
    (this.$refs.cropsMap as any).showPlots(event);
  }

  private showInfractionsInMap(geojson: Object) {
    if ([geojson] !== this.infractionsGeojson) {
      this.infractionsGeojson = [geojson];
    }
  }

  private removeInfractionsFromMap() {
    this.infractionsGeojson = [];
  }
  
  get resultList() {
    return this.results && this.results.length > 0 ? this.results : this.result ? [this.result] : undefined;
  }

  private trackResult() {
    this.polling = setTimeout(async () => {
      if (!this.result && (!this.status || !this.status.finishedWithError)) {
        await this.loadResult();
      }

      if (!this.status || this.status.processing) {
        await this.loadStatus();
      }

      if (this.needsTracking) {
        this.trackResult();
      }
    }, 7000);
  }

  private beforeDestroy() {
    if (this.polling) {
      clearTimeout(this.polling);
      this.polling = null;
    }
  }

  private async loadResult(force: boolean = true) {
    try {
      if (force || (!this.result && (!this.status || !this.status.finishedWithError))) {
        this.loading = true;
        const results = await resultsService.getFarmAnalysisResultList(this.farmAnalysis.uuid, this.harvestSeason);
        this.result = results.find((result) => result.cropType === this.currentCropType) ?? results[0];
        this.results = results;
        this.url = this.result && this.result.s3GeoJsonUrl ? this.result.s3GeoJsonUrl : '';
      }
    } catch {} finally {
      this.loading = false;
    }
  }

  private async loadStatus() {
    try {
      if (!this.status || this.status.processing) {
        this.status = await statusService.getByFarmAnalysisUuid(
          this.analysis.uuid,
          this.farmAnalysis.uuid
        );
      }
    } catch {}
  }

  private get needsTracking() {
    return (
      (!this.result && (!this.status || !this.status.finishedWithError)) ||
      !this.status ||
      this.status.processing
    );
  }

  private get cropsList(): CropsDTO[] {
    return this.$store.getters[`crops/crops`](this.farmAnalysis.uuid);
  }

  private get selectedCropsUuid() {
    return this.selectedCrops.flatMap((cropLabel) => {
      return (
        this.cropsList.find((crop) => `${crop.cropLabel}` === cropLabel)
          ?.cropUuid || []
      );
    });
  }

  private get geoJson() {
    const props = {
      id: this.analysis!.farmUuid,
      title: this.analysis!.farmName,
      fill: COLORS[this.colorIndex],
      "fill-opacity": 0.2,
      stroke: COLORS[this.colorIndex],
      "stroke-width": 4,
    };
    return wrapGeometryObject(this.analysis!.farmGeom, props);
  }

  private get geoJsonFileName() {
    return `${this.analysis.farmName}_${this.farmAnalysis.analysisYear}_${
      this.result?.cropType ?? "ANY"
    }.geojson`;
  }

  private get averageTemperatureData(): WeatherData[] {
    if (!this.untilDate || !this.fromDate) {
      return [];
    }
    return this.$store.state.weather.periodTemperatureAverageContext
      .get(weatherService.getPeriodKey(this.analysis!.farmUuid, this.fromDate.getFullYear(), this.untilDate.getFullYear())) ?? [];
  }

  private get historicalTemperatureData(): WeatherData[] {
    if (!this.untilDate) {
      return [];
    }
    return this.$store.state.weather.historicalTemperatureAverageContext
      .get(weatherService.getHistoricalKey(this.analysis!.farmUuid, this.untilDate.getFullYear())) ?? [];
  }

  private get averageSPI4Data(): WeatherData[] {
    if (!this.untilDate || !this.fromDate) {
      return [];
    }
    return this.$store.state.weather.periodSPI4AverageContext
      .get(weatherService.getPeriodKey(this.analysis!.farmUuid, this.fromDate.getFullYear(), this.untilDate.getFullYear())) ?? [];
  }

  private get historicalSPI4Data(): WeatherData[] {
    if (!this.untilDate) {
      return [];
    }
    return this.$store.state.weather.historicalSPI4AverageContext
      .get(weatherService.getHistoricalKey(this.analysis!.farmUuid, this.untilDate.getFullYear())) ?? [];
      
  }

  private async loadWeatherData() {
    const analysisYear = this.farmAnalysis.analysisYear;
    const fromDate = new Date(analysisYear - 1, 6, 1);
    const untilDate = new Date(analysisYear, 7, 31);

    this.fromDate = fromDate;
    this.untilDate = untilDate;

    await Promise.all([
      (async () => {
        await weatherService.fetchPeriodTemperatureAverage(
          this.analysis!.farmUuid,
          fromDate,
          untilDate
        );
        await weatherService.fetchHistoricalTemperatureAverage(
          this.analysis!.farmUuid,
          untilDate
        );
      })(),
      (async () => {
        await weatherService.fetchPeriodSPI4Average(
          this.analysis!.farmUuid,
          fromDate,
          untilDate
        )
        await weatherService.fetchHistoricalSPI4Average(
          this.analysis!.farmUuid,
          untilDate
        );
      })(),
    ]);
  }
}
